import { useState, useMemo } from 'react';
import { Share2, Check, Instagram } from 'lucide-react';
import InstagramShareModal from './InstagramShareModal';
import { track } from '../lib/analytics';
import { getPublicOrigin } from '../lib/nativeBridge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ShareToStoryButton
 * ─────────────────────────────────────────────────────────────────
 * Three-action share row for stories, athlete pages, FLYT clients, etc.
 *
 *   ┌─────────┬───────────────┬──────────┐
 *   │ Share   │  Instagram    │  Copy    │
 *   └─────────┴───────────────┴──────────┘
 *
 *   • Share      — native Web Share API
 *   • Instagram  — opens an intricate preview modal showing exactly
 *                  how the IG Story Link-Sticker will render, with
 *                  one-tap copy of the OG-rich /api/share/stories/{slug}
 *                  URL and a deep link into the Instagram app. The
 *                  shared URL renders a real preview card on IG,
 *                  iMessage, Twitter, Discord, WhatsApp, etc.
 *   • Copy       — explicit "copy URL to clipboard" action.
 */
const ShareToStoryButton = ({
  title,
  subtitle,
  url,                       // canonical article URL (used for Share + Copy)
  variant = 'dark',
  className = '',
  image,
  channelId,
  // Pass story.slug to enable the rich Instagram unfurl URL. If absent,
  // we fall back to sharing the canonical `url` (works fine, but no
  // server-rendered OG card for crawlers).
  slug,
  // category is accepted for backwards compatibility but unused now
  category,
}) => {
  const [shareState, setShareState] = useState('idle');
  const [igOpen, setIgOpen] = useState(false);

  // The OG-rich URL — this is what gets pasted into Instagram so the
  // sticker renders a real preview card AND what we hand to native share
  // so iMessage/WhatsApp/etc. fetch server-rendered OG tags (so the
  // story title — not the generic site title — appears under the image).
  // Prefer the CURRENT host so a user on flythouse.com gets a
  // flythouse.com link; only fall back to the canonical share domain
  // when window isn't available (SSR safety).
  const igShareUrl = useMemo(() => {
    if (!slug) return url;
    // iter307 · Use `getPublicOrigin()` so shared URLs on native iOS
    // never encode `capacitor://localhost` — Messages / Instagram
    // can't resolve that scheme. On web this still uses the current
    // brand host, so multi-brand sharing UX is unchanged.
    return `${getPublicOrigin()}/api/share/stories/${slug}`;
  }, [slug, url]);

  // Canonical article URL — same public-origin rule as igShareUrl.
  const canonicalArticleUrl = useMemo(() => {
    if (!url) return url;
    try {
      const u = new URL(url);
      return `${getPublicOrigin()}${u.pathname}${u.search}${u.hash}`;
    } catch { return url; }
  }, [url]);

  const destinationHost = useMemo(() => {
    if (typeof window !== 'undefined' && window.location?.host) return window.location.host;
    try { return new URL(url).host; } catch { return 'flythouse.com'; }
  }, [url]);

  const brandLabel = channelId === 'flyt'
    ? 'FLYT Studios'
    : channelId === 'dreams_visionary'
      ? 'Dreams Visionary'
      : 'Dreams League';

  const handleShare = async () => {
    if (!url) return;
    // ALWAYS share the OG-rich URL when we have a slug so the preview
    // card pops everywhere the user shares (iMessage/WhatsApp/Discord/X).
    // The server-rendered HTML at /api/share/stories/{slug} contains the
    // story-specific og:title so the link unfurls under the image with
    // the right title — not the generic site title.
    const sharedUrl = slug ? igShareUrl : canonicalArticleUrl;
    // Plain-text fallback for apps that don't read OG (e.g. plain SMS).
    // Includes the title so the message still reads well as text-only.
    const plainText = subtitle
      ? `${title || ''} — ${subtitle}`.replace(/\s+—\s+$/,'').trim()
      : (title || 'Dreams League');
    const payload = {
      title: title || 'Dreams League',
      text: plainText,
      url: sharedUrl,
    };
    try {
      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share(payload);
        track('story_shared', { share_channel: 'native_share', url: sharedUrl, title });
        setShareState('shared');
      } else if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(sharedUrl);
        track('story_shared', { share_channel: 'copy_link', url: sharedUrl, title });
        setShareState('copied');
      } else {
        window.location.href = `mailto:?subject=${encodeURIComponent(title || '')}&body=${encodeURIComponent(plainText + '\n' + sharedUrl)}`;
        track('story_shared', { share_channel: 'mailto', url: sharedUrl, title });
        setShareState('shared');
      }
    } catch (e) {
      if (e?.name === 'AbortError') { setShareState('idle'); return; }
      setShareState('error');
    } finally {
      setTimeout(() => setShareState('idle'), 1800);
    }
  };

  const lightTheme = variant === 'light';
  const sharePillBase = lightTheme
    ? 'bg-black text-white hover:bg-gray-800'
    : 'bg-white text-black hover:bg-gray-100';

  const shareLabel = (() => {
    switch (shareState) {
      case 'shared': return 'Sent!';
      case 'copied': return 'Link copied';
      case 'error':  return 'Try again';
      default:       return 'Share';
    }
  })();

  const ShareIcon = shareState === 'shared' || shareState === 'copied' ? Check : Share2;

  return (
    <>
      <div className={`inline-flex items-center gap-2 flex-wrap ${className}`} data-testid="share-row">
        <button
          type="button"
          onClick={handleShare}
          className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm transition active:scale-95 ${sharePillBase}`}
          data-testid="share-link-btn"
          aria-label="Share this link"
        >
          <ShareIcon className="w-4 h-4" />
          {shareLabel}
        </button>

        <button
          type="button"
          onClick={() => setIgOpen(true)}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm text-white transition active:scale-95"
          style={{
            background: 'linear-gradient(95deg, #f9ce34 0%, #ee2a7b 45%, #6228d7 100%)',
            boxShadow: '0 6px 18px rgba(238, 42, 123, 0.28)',
          }}
          data-testid="share-instagram-btn"
          aria-label="Share to Instagram Story"
        >
          <Instagram className="w-4 h-4" />
          Instagram
        </button>
      </div>

      <InstagramShareModal
        open={igOpen}
        onClose={() => setIgOpen(false)}
        shareUrl={igShareUrl}
        title={title}
        subtitle={subtitle}
        image={image}
        brandLabel={brandLabel}
        destinationHost={destinationHost}
      />
    </>
  );
};

export default ShareToStoryButton;
